import React, {useState, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
import {Route} from 'react-router-dom';

import {logger} from '@stubhub/react-store-provider';
import {post} from '@stubhub/rest-method';

import {clearSession, hasValidError, setCookiesData} from './utils';

export const CustomRedirect = ({redirect, defaultSignUp}) => {
  if (typeof window !== 'undefined') {
    let baseUrl = `${process.env.REACT_APP_LOGIN_URL || `${window.location.origin}/login`}`;
    const redirectUrl = `${window.location.origin + redirect.pathname + redirect.search}`;
    if (defaultSignUp) {
      baseUrl += '/signup';
    }

    window.location.href = `${baseUrl}?redirect=${encodeURIComponent(redirectUrl)}`;
  }

  return null;
};

CustomRedirect.propTypes = {
  /**
   * Contains information about the location
   */
  redirect: PropTypes.object,

  /**
   * If redirection is to sign up form
   */
  defaultSignUp: PropTypes.bool,
};

const PrivateRoute = ({component: Component, render, cookies, defaultSignUp = false, ...rest}) => {
  const [showLogin, setShowLogin] = useState(undefined);

  const updateLoginFormVisibility = (visible) => {
    setShowLogin(visible);
  };

  const validateSession = useCallback(() => {
    post({
      host: `${process.env.REACT_APP_LOGIN_URL || `${window.location.origin}/login`}`.replace(
        '/login',
        '/bfx-login/api'
      ),
      path: '/login/session/validate',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      },
      logger,
    })
      .then(() => updateLoginFormVisibility(false))
      .catch((error) => {
        if (hasValidError(error) && cookies) {
          setCookiesData(cookies, 'session_loginStatus', false);
          clearSession(cookies);
        }
        updateLoginFormVisibility(true);
      });
  }, [cookies]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  return (
    <Route
      {...rest}
      render={(props) =>
        showLogin ? (
          <CustomRedirect redirect={props.location} defaultSignUp={defaultSignUp} />
        ) : showLogin === undefined ? null : Component ? (
          <Component {...props} />
        ) : (
          render && render(props)
        )
      }
    />
  );
};

PrivateRoute.propTypes = {
  /**
   * Renders a React component when the route matches
   */
  component: PropTypes.elementType,

  /**
   * Renders a function that returns a React element when the route matches
   */
  render: PropTypes.func,

  /**
   * Represents the location object provided by React Router
   */
  location: PropTypes.object,

  /**
   * Represents the cookies object
   */
  cookies: PropTypes.object,

  /**
   * If redirection is to sign up form
   */
  defaultSignUp: PropTypes.bool,
};

export default PrivateRoute;
